import { api } from '@/lib/api'
import type { UUID } from '@/types/api'

import type { ChannelListResponse, ChatChannel, ChatMessage, MessageListResponse } from './types'

function base(slug: string): string {
  return `/api/v2/orgs/${encodeURIComponent(slug)}/chat`
}

export const chatApi = {
  listChannels: (slug: string) => api.get<ChannelListResponse>(`${base(slug)}/channels`),

  createDm: (slug: string, userId: number) =>
    api.post<ChatChannel>(`${base(slug)}/channels`, { kind: 'dm', user_id: userId }),

  createGroup: (slug: string, input: { name: string; member_ids: number[] }) =>
    api.post<ChatChannel>(`${base(slug)}/channels`, { kind: 'group', ...input }),

  getChannel: (slug: string, id: UUID) =>
    api.get<ChatChannel>(`${base(slug)}/channels/${encodeURIComponent(id)}`),

  renameChannel: (slug: string, id: UUID, name: string) =>
    api.patch<ChatChannel>(`${base(slug)}/channels/${encodeURIComponent(id)}`, { name }),

  deleteChannel: (slug: string, id: UUID) =>
    api.delete<{ message: string }>(`${base(slug)}/channels/${encodeURIComponent(id)}`),

  addMembers: (slug: string, id: UUID, userIds: number[]) =>
    api.post<ChatChannel>(`${base(slug)}/channels/${encodeURIComponent(id)}/members`, {
      user_ids: userIds,
    }),

  removeMember: (slug: string, id: UUID, userId: number) =>
    api.delete<{ message: string }>(
      `${base(slug)}/channels/${encodeURIComponent(id)}/members/${userId}`,
    ),

  listMessages: (
    slug: string,
    id: UUID,
    query: { before?: number; after?: number; limit?: number } = {},
  ) =>
    api.get<MessageListResponse>(`${base(slug)}/channels/${encodeURIComponent(id)}/messages`, {
      query,
    }),

  postMessage: (slug: string, id: UUID, body: string) =>
    api.post<ChatMessage>(`${base(slug)}/channels/${encodeURIComponent(id)}/messages`, { body }),

  editMessage: (slug: string, messageId: number, body: string) =>
    api.patch<ChatMessage>(`${base(slug)}/messages/${messageId}`, { body }),

  deleteMessage: (slug: string, messageId: number) =>
    api.delete<ChatMessage>(`${base(slug)}/messages/${messageId}`),

  markRead: (slug: string, id: UUID, lastReadId?: number) =>
    api.post<void>(
      `${base(slug)}/channels/${encodeURIComponent(id)}/read`,
      lastReadId ? { last_read_id: lastReadId } : undefined,
    ),
}
